import { ProductItem } from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';
import { productToPriceComponents } from 'lane-shared/domains/billingPayments/helpers';
import {
  getPricing,
  normalizeOverrideSellingPrice,
} from 'domains-billing-calculation';

/**
 * Create an updated product item with recalculated pricing
 * after a rate override
 */
export const createUpdatedProductItem = (
  item: ProductItem,
  overrideAmount: number,
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  userInputRate?: number,
  isAdminFeeFeatureEnabled = false
): ProductItem => {
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  let originalPrice = item.product.originalPrice;

  if (userInputRate !== undefined && !item.isRateOverridden) {
    if (!originalPrice || item.product.sellingPrice?.listPrice !== undefined) {
      originalPrice = {
        amount: item.product.amount,
        markup: item.product.markup || undefined,
        tax: item.product.tax || undefined,
        adminFee: item.product.adminFee || undefined,
        adminFeeTax: item.product.adminFeeTax || undefined,
      };
    }
  }

  const updatedProduct = {
    ...item.product,
    amount: overrideAmount,
    sellingPrice: undefined,
    ...(originalPrice ? { originalPrice } : {}),
  };

  const priceComponents = productToPriceComponents(updatedProduct);

  const pricing = getPricing({
    currency,
    locale,
    priceComponents,
    isAdminFeeFeatureEnabled,
  });

  const sellingPrice = normalizeOverrideSellingPrice({
    amount: overrideAmount,
    pricing,
    userInputRate,
  });

  return {
    ...item,
    product: {
      ...updatedProduct,
      amount: roundToTwoDecimals(overrideAmount),
      sellingPrice,
    },
    isRateOverridden: true,
    userInputRate: userInputRate ?? sellingPrice.listPrice,
  };
};
